import { useEffect, useState } from "react";
import { Form, Button, Row, Col, Card, Alert, Spinner } from "react-bootstrap";

const SuppliesForm = ({
    supplyMap,
    clientAddresses,
    onSubmit
}) => {
    const addressObj = {
        label: "Custom",
        street: '',
        city: '',
        state: '',
        zipCode: '',
        custom: true
    };
    
    const [isInitialized, setIsInitialized] = useState(false);
    const [supplyList, setSupplyList] = useState({});
    const [address, setAddress] = useState({...addressObj});
    const [addressList, setAddressList] = useState([{...addressObj}]);
    const [specialRequest, setSpecialRequest] = useState('');
    const [isFormValid, setIsFormValid] = useState(false);
    const [alert, setAlert] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const init = async () => {
            getSupplyList();
            const formattedAddresses = clientAddresses.map(a => ({
                label: a.label,
                street: a.address,
                city: a.city,
                state: a.state,
                zipCode: a.zipCode,
                custom: false
            }));
            const newAddressList = [...formattedAddresses, {...addressObj}];
            setAddressList(newAddressList);
            setAddress(newAddressList[0]);
            setIsInitialized(true);
        }
        if (!isInitialized) {
            init();
        }
        checkFormValidity();
    }, [supplyList, address, isFormValid]);

    const getSupplyList = () => {
        const formattedSupplyMap = {}; 
        for (let supply of Object.values(supplyMap)) {
            if (!supply.disabled) {
                formattedSupplyMap[supply.id] = {
                    supply: supply.id,
                    label: supply.labelEN,
                    refCodes: supply.refCodes,
                    value: 0
                };
            }
        }
        setSupplyList(formattedSupplyMap);
    };

    const resetForm = async () => {
        try {
           getSupplyList();
            const formattedAddresses = clientAddresses.map(a => ({
                label: a.label,
                street: a.address,
                city: a.city,
                state: a.state,
                zipCode: a.zipCode,
                custom: false
            }));
            const newAddressList = [...formattedAddresses, {...addressObj}];
            setAddressList(newAddressList);
            setAddress(newAddressList[0]); 
            setIsFormValid(false);
        } catch(e){}
    }

    const handleSubmit = async (event) => {
        if (!isFormValid) return;
        setIsSubmitting(true);
        try {
            event.preventDefault();
            const formattedSuppliesOrder = {
                street: address.street,
                city: address.city,
                state: address.state,
                zipCode: address.zipCode,
                specialRequest: specialRequest,
                supplies: Object.values(supplyList).map((item) => {
                    return { supply: item.supply, quantity: item.value }
                })
            };
            await onSubmit(formattedSuppliesOrder);
            // reset supply list and get up to date supply list.
            setIsSubmitting(false);
            await resetForm();
            setAlert({error: false, message: 'Order successfully created.'});
        } catch (err) {
            setAlert({error: true, message: 'Impossible to create order, try again later.'})
            setIsSubmitting(false);
        }
    }

    const onAlertClose = () => {
        setAlert(null);
    }

    const checkFormValidity = () => {
        if (address.street.trim().length === 0) {
            setIsFormValid(false);
            return;
        }
        if (address.city.trim().length === 0) {
            setIsFormValid(false);
            return;
        }
        if (address.state.trim().length === 0) {
            setIsFormValid(false);
            return;
        }
        if (address.zipCode.trim().length !== 5) {
            setIsFormValid(false);
            return;
        }
        const totalQuantity = Object.values(supplyList).reduce((acc, curr) => acc += curr.value, 0);
        if (totalQuantity === 0) {
            setIsFormValid(false);
            return;
        }
        setIsFormValid(true);
    }

    const onAddressSelect = (event) => {
        const { value } = event.target;
        setAddress(addressList[Number(value)]);
    };

    const onAddressChange = (event) => {
        const { name , value } = event.target;
        const newAddress = {
            ...address,
            [name]: value,
        };
        setAddress(newAddress);
    };

    const onSupplyChange = (event) => {
        let { value, id } = event.target;
        const targetedSupply = supplyList[id];
        if (value === '' || Number.isNaN(Number(value))) value = 0;

        if (targetedSupply) {
            value = Number(value);
            if (value < 0) value = 0;
            if (value > 100) value = 100;

            targetedSupply.value = value;

            const newSuppliesObj = {
                ...supplyList,
                [id]: targetedSupply
            };

            setSupplyList(newSuppliesObj);
        }
    };

    const onSpecialRequestChange = (event) => {
        const { value } = event.target
        setSpecialRequest(value);
    };

    return (
        <div>
            <Card className="p-3 m-2">
                {isInitialized && (
                    <>
                        {alert && (
                            <Alert 
                                variant={alert.error ? 'danger' : 'success'}
                                onClose={onAlertClose}
                                dismissible
                            >
                                {alert.message}
                            </Alert>
                        )}
                        <Form onSubmit={handleSubmit}>
                            <Row>
                                <Form.Group as={Row}>
                                    <Col lg={6}>
                                        <Form.Label>Select Delivery Address</Form.Label>
                                    </Col>
                                    <Col lg>
                                        <Form.Select onChange={onAddressSelect}>
                                            {Object.entries(addressList).map(([key, addr]) =>
                                                (<option value={key} key={`addr-${key}`}>{addr.label}</option>)
                                            )}
                                        </Form.Select>
                                    </Col>
                                </Form.Group>
                                <Row>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Street</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="street"
                                            key="street"
                                            value={address.street}
                                            onChange={onAddressChange}
                                            disabled={!address.custom}
                                        />
                                    </Form.Group>
                                </Row>
                                <Form.Group as={Row}>
                                    <Col lg={6}>
                                        <Form.Label>City</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="city"
                                            key="city"
                                            value={address.city}
                                            onChange={onAddressChange}
                                            disabled={!address.custom}
                                        />
                                    </Col>
                                    <Col lg={4}>
                                        <Form.Label>State</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="state"
                                            key="state"
                                            value={address.state}
                                            onChange={onAddressChange}
                                            disabled={!address.custom}
                                        />
                                    </Col>
                                    <Col lg={2}>
                                        <Form.Label>Zip</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="zipCode"
                                            key="zipCode"
                                            value={address.zipCode}
                                            onChange={onAddressChange}
                                            disabled={!address.custom}
                                        />
                                    </Col>
                                </Form.Group>
                            </Row>
                            <Row className="mt-4 align-items-center">
                                {!isInitialized && (
                                    <>
                                        <Spinner animation="border" role="status"></Spinner>
                                        <p>Loading Supplies...</p>
                                    </>
                                )}
                                {
                                    Object.entries(supplyList).map(([key, supply]) => {
                                        return (
                                            <Col lg={6} key={key}>
                                                <Form.Group className="mb-2 d-flex" as={Row}>
                                                    <Col lg={3} className="align-self-center">
                                                        <Form.Control
                                                            className="pe-2 text-right"
                                                            type="text"
                                                            pattern="[0-9]*"
                                                            key={key}
                                                            id={key}
                                                            name={supply.name}
                                                            onChange={onSupplyChange}
                                                            value={supply.value}
                                                        />
                                                    </Col>
                                                    <Form.Label column className="ps-1" lg="9">
                                                        <span>
                                                            {supply.label} <span className="fw-bold fst-italic">{supply.refCodes.join(', ')}</span>
                                                        </span>
                                                    </Form.Label>
                                                </Form.Group>
                                            </Col>
                                        );
                                    })
                                }
                            </Row>
                            <Row className="mt-2">
                                <Form.Group className="mb-3">
                                    <Form.Label>Special Request</Form.Label>
                                    <Form.Control as="textarea" rows={2} value={specialRequest} onChange={onSpecialRequestChange}/>
                                </Form.Group>
                            </Row>
                            <Button 
                                disabled={!isFormValid || isSubmitting}
                                variant="primary" 
                                type="submit" 
                                className="mt-2"
                            >
                                {isSubmitting ?
                                    (
                                        <>
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                                className='me-2'
                                            />
                                            Sending order...
                                        </>
                                    ) : 
                                    (
                                        <>Submit</>
                                    )

                                }
                            </Button>
                        </Form>
                    </>
                )}
            </Card>
        </div>
    );
};

export default SuppliesForm;
